import { useVerifyPostAccess } from '@/graphql/mutations/Utils.mutation';
import type { Maybe, VerifyPostAccessInput } from '@/graphql/types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Container, Link, Paper, Typography } from '@mui/material';
import { createLink } from '@tanstack/react-router';
import { useEffect } from 'react';
import { RequestOTPAccess } from './request-otp-access';

const MuiLinkComponent = createLink(Link);

type Props = {
  id: Maybe<string>;
  input: VerifyPostAccessInput;

  onInitVerifyPostAccess?: () => void;
  onOTPSuccess?: () => void;

  children: React.ReactNode;
};

const AccessControlWrapper = (props: Props) => {
  const [verifyPostAccess, isVerifyPostAccessLoading, data] = useVerifyPostAccess();

  useEffect(() => {
    if (!!props?.id || isVerifyPostAccessLoading) {
      return;
    }

    props?.onInitVerifyPostAccess?.();

    verifyPostAccess({
      input: props.input,
    });
  }, [props?.id]);

  useEffect(() => {
    if (!!props?.id) {
      const url = new URL(window.location.href);

      if (url.searchParams.has('zntkn')) {
        // remove the zntkn query parameter from the URL
        url.searchParams.delete('zntkn');
        window.history.replaceState(
          {
            ...window.history.state,
          },
          '',
          url.toString()
        );
      }
    }
  }, []);

  if (
    !props?.id &&
    props?.input?.id &&
    data?.verifyPostAccess?.uiPermission === 'OTP' &&
    (data?.verifyPostAccess?.sessionActive === false || data?.verifyPostAccess?.hasAccess === false)
  ) {
    return (
      <RequestOTPAccess
        type={props.input.postType}
        id={props.input.id}
        onSuccess={() => {
          props.onOTPSuccess?.();
        }}
      />
    );
  }

  if (!props?.id && data?.verifyPostAccess?.uiPermission === 'TOKEN') {
    return (
      <Container maxWidth="md" sx={{ my: 6 }}>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            <ErrorOutlineIcon color="action" sx={{ fontSize: 40, opacity: 0.8 }} />
          </Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'normal' }}>
            Access Restricted
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '500px', mx: 'auto' }}>
            You don't have permission to view this content. If you have a working link please try using the link again.
            Otherwise please contact an administrator if you believe this is an error.
          </Typography>
          <Button variant="text" color="primary" component={MuiLinkComponent} to="/" size="small" sx={{ mt: 2 }}>
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return props.children;
};

export { AccessControlWrapper };
